<template>
  <SfDropdown
    align-option="l"
    drawer-direction="left"
    :dropdown-id="dropdownId"
    :is-open="isOpen"
    variant="seamless"
    class="period-selection-date-type-dropdown"
    @dropdown-trigger-clicked="handleToggleDropdown"
  >
    <template #trigger>
      <PeriodSelectionTrigger
        :period-selection-option="periodSelectionOption"
        :is-parent-selected="isParentSelected"
        :value="dateTitle"
      >
        <SfButtonDs variant="secondary" size="sm" type="button">
          {{ dateTitle }}
          <span class="fa fa-caret-down tw-ml-1.5 tw-shrink-0" />
        </SfButtonDs>
      </PeriodSelectionTrigger>
    </template>

    <template #menu>
      <SfDropdownItem
        v-for="dateTypeOption in dateTypeOptions"
        :key="dateTypeOption.key"
        :has-checkmark="selectedDateType === dateTypeOption.key"
        @click="handleDateTypeSelect(dateTypeOption.key)"
      >
        {{ I18n.t(dateTypeOption.titleKey) }}
      </SfDropdownItem>

      <DatePicker
        v-if="isParentSelected"
        :value="ledgerSelectDate"
        :options="datepickerOptions"
        :focused-at="datepickerFocusedAt"
        :hidden-input="true"
        name="period"
        @update:model-value="handleDateSelected"
      ></DatePicker>
    </template>
  </SfDropdown>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import PeriodSelectionTrigger from './period-selection-trigger.vue'
import type { DateType, PeriodSelectionOption } from '../types'
import DatePicker from '@app/shared/vue/components/form/date-picker.vue'
import { dateTypes } from '../types'
import { dateTypeOptions } from '../constants'

const I18n = useI18n()

interface Props {
  dropdownId: string
  isOpen: boolean
  isParentSelected: boolean
  periodSelectionOption: PeriodSelectionOption
  ledgerSelectDate: string | null
  selectedDateType?: DateType
}
const props = defineProps<Props>()
const datepickerFocusedAt = ref(0)

const dateTitle = computed(() => {
  if (props.selectedDateType === dateTypes.FIXED_DATE) {
    return props.ledgerSelectDate ?? ''
  }

  return I18n.t(`companies_overview.period_selection_new.today`)
})

const datepickerOptions = {
  focus: true,
}

const emit =
  defineEmits<{
    'date-type-selected': [dateType: DateType]
    'date-selected': [date: string]
    'toggle-dropdown': []
  }>()

const handleDatepickerFocus = (focus: boolean) => {
  if (focus) {
    datepickerFocusedAt.value = Date.now()
  } else {
    datepickerFocusedAt.value = 0
  }
}
const handleDateTypeSelect = (dateType: DateType) => {
  handleDatepickerFocus(dateType === dateTypes.FIXED_DATE)
  emit('date-type-selected', dateType)
}

const handleDateSelected = (date: string) => {
  emit('date-selected', date)
}

const handleToggleDropdown = () => {
  emit('toggle-dropdown')

  // handle the focus after isOpen (dropdown) is updated (vue batches reactivity updates)
  nextTick(() => {
    if (props.selectedDateType === dateTypes.FIXED_DATE) {
      handleDatepickerFocus(props.isOpen)
    }
  })
}
</script>
<style>
.period-selection-date-type-dropdown .sf-dropdown2-menu {
  min-width: initial;
  width: auto;
}

.flatpickr-calendar {
  transform: translateY(16px);
}
</style>
